import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from "../services/apiHelpers";
import DoctorVitalsSettings from "../components/DoctorVitalsSettings";
import {
  Activity,
  AlertCircle,
  Ban,
  Banknote,
  CalendarClock,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  Crown,
  FileText,
  Mail,
  MessageCircle,
  MoreVertical,
  Phone,
  Plus,
  Receipt,
  Search,
  Smartphone,
  Stethoscope,
  User,
  UserCheck,
  Users,
  UserX,
  Wallet,
  X,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Constants & helpers (unchanged logic)                              */
/* ------------------------------------------------------------------ */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SPECIALIZATIONS = ["General Physician", "Dermatologist", "Orthopedic Surgeon", "Pediatrician", "Cardiologist", "ENT Specialist"];
const createEmptySession = () => ({ from: "", fromMinute: "00", fromPeriod: "AM", to: "", toMinute: "00", toPeriod: "AM" });
const createEmptyPrescriptionSchedule = () => WEEKDAYS.reduce((result, day) => {
  result[day] = { session1: createEmptySession(), session2: createEmptySession(), holiday: false };
  return result;
}, {});
const normalizePrescriptionSchedule = (raw = {}) => WEEKDAYS.reduce((result, day) => {
  const value = raw[day] || {};
  result[day] = {
    holiday: Boolean(value.holiday),
    session1: { ...createEmptySession(), ...(value.session1 || {}) },
    session2: { ...createEmptySession(), ...(value.session2 || {}) },
  };
  return result;
}, {});
export const normalizeDoctorStatus = (doctor = {}) => {
  const inactive = doctor.is_active === false || doctor.is_active === 0 || doctor.is_active === "0";
  if (inactive) return "Inactive";

  const active = doctor.is_active === true || doctor.is_active === 1 || doctor.is_active === "1"
    || doctor.scheduled_today === true || doctor.scheduled_today === 1 || doctor.scheduled_today === "1";
  return active ? "Active" : "Inactive";
};

export const getDoctorStatusOptions = () => ["All Status", "Active", "Inactive"];

const emptyDoctor = () => ({
  id: null, name: "", gender: "Male", specialization: SPECIALIZATIONS[0], qualification: "", experience: "", status: "Active",
  patientsToday: 0, fee: "", mobile: "", email: "", registrationNumber: "", schedule: {},
  upiId: "", upiPayeeName: "", enableUpiPayments: true, enableCashPayments: true, enablePatientReceipts: false, enableEPrescription: true, enableWhatsAppNotifications: true,
  prescriptionSchedule: createEmptyPrescriptionSchedule(),
});

const STATUS_STYLES = {
  Active: ["#e8f8ef", "#15803d", "#22c55e"],
  Inactive: ["#feecec", "#dc2626", "#ef4444"],
};

const getInitials = (name = "") => {
  const parts = name.replace(/^dr\.?\s+/i, "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

/* ------------------------------------------------------------------ */
/*  Small presentational components                                    */
/* ------------------------------------------------------------------ */
function StatusPill({ status }) {
  const [background, color, dot] = STATUS_STYLES[status] || STATUS_STYLES.Active;
  return <span className="dm-status" style={{ background, color }}><i style={{ background: dot }} />{status}</span>;
}

function Avatar({ size = 52, src = "", alt = "", initials = "" }) {
  return (
    <span className="dm-avatar" style={{ width: size, height: size, fontSize: size * 0.34 }}>
      {src ? <img src={src} alt={alt} /> : initials ? <span>{initials}</span> : <User size={size * 0.46} />}
    </span>
  );
}

function Field({ label, required, icon: Icon, prefix, chevron, full, children }) {
  return (
    <label className={`dm-field${full ? " full" : ""}`}>
      <span className="dm-label">{label}{required && <b>*</b>}</span>
      <div className={`dm-control${Icon ? " has-icon" : ""}`}>
        {Icon && <Icon size={15} className="dm-control-icon" />}
        {prefix && <em className="dm-prefix">{prefix}</em>}
        {children}
        {chevron && <ChevronDown size={15} className="dm-chevron" />}
      </div>
    </label>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="dm-section-title">
      <span><Icon size={16} /></span>
      <div><strong>{title}</strong><small>{subtitle}</small></div>
    </div>
  );
}

function Switch({ checked, onChange, disabled }) {
  return (
    <span className="dm-switch">
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <i />
    </span>
  );
}

const FEATURES = [
  ["Enable UPI Payments", "enableUpiPayments", Smartphone, "Accept consultation payments through UPI"],
  ["Enable Cash Payments", "enableCashPayments", Banknote, "Accept consultation payments in cash"],
  ["Patient Receipts", "enablePatientReceipts", Receipt, "Give patients a receipt after payment"],
  ["E-Prescription", "enableEPrescription", FileText, "Create digital prescriptions for this doctor"],
  ["WhatsApp Notifications", "enableWhatsAppNotifications", MessageCircle, "Send updates to patients on WhatsApp"],
];

const TABS = [["personal", User, "Personal"], ["schedule", CalendarClock, "Schedule"], ["fees", Wallet, "Consultation Fees"], ["features", CheckCircle2, "Payment & Features"], ["vitals", Activity, "Vitals"]];

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
export default function DoctorsManagementPage() {
  const { auth } = useAuth();
  const clinicId = auth?.clinic?.id;
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [doctorPhotos, setDoctorPhotos] = useState({});
  const [capacity, setCapacity] = useState(null);
  const [patientReceiptsAvailable, setPatientReceiptsAvailable] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyDoctor);
  const [panelOpen, setPanelOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [search, setSearch] = useState("");
  const [specialization, setSpecialization] = useState("All Specializations");
  const [status, setStatus] = useState("All Status");
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const photoInputRef = useRef(null);
  const photoUrlRef = useRef("");

  const assetHeaders = useMemo(() => ({ "X-Clinic-Id": String(clinicId || "") }), [clinicId]);

  const applyPhoto = useCallback((url) => {
    if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    photoUrlRef.current = url;
    setPhotoUrl(url);
  }, []);

  useEffect(() => () => { if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current); }, []);

  const mapDoctor = (doctor) => ({
    ...emptyDoctor(),
    ...doctor,
    id: doctor.id,
    name: doctor.doctor_name || doctor.name || "",
    specialization: doctor.specialization || "",
    qualification: doctor.qualification || "",
    gender: doctor.gender || "OTHER",
    experience: doctor.experience_years ?? doctor.experience ?? "",
    fee: doctor.consultation_fee ?? doctor.fee ?? "",
    mobile: doctor.doctor_phone || doctor.mobile || "",
    email: doctor.doctor_email || doctor.email || "",
    registrationNumber: doctor.registration_number || doctor.registrationNumber || "",
    status: normalizeDoctorStatus(doctor),
    upiId: doctor.upi_id || doctor.upiId || "",
    upiPayeeName: doctor.upi_payee_name || doctor.upiPayeeName || "",
    enableUpiPayments: doctor.enable_upi_payments !== false,
    enableCashPayments: doctor.enable_cash_payments !== false,
    enablePatientReceipts: doctor.enable_patient_receipts === true,
    enableEPrescription: doctor.enable_e_prescription !== false,
    enableWhatsAppNotifications: doctor.enable_whatsapp_notifications !== false,
    prescriptionSchedule: (() => {
      try { return normalizePrescriptionSchedule(doctor.default_prescription_schedule ? JSON.parse(doctor.default_prescription_schedule) : {}); } catch { return createEmptyPrescriptionSchedule(); }
    })(),
    patientsToday: doctor.patients_today ?? doctor.patientsToday ?? 0,
    workingHours: doctor.working_hours || "",
    scheduledToday: doctor.scheduled_today === true || doctor.scheduled_today === 1 || doctor.scheduled_today === "1",
  });

  const loadBackendDoctors = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const headers = { "X-Clinic-Id": String(clinicId) };
      const [doctorResponse, capacityResponse, subscriptionResponse] = await Promise.all([
        fetch(buildApiUrl(`doctors/by-clinic-id/${clinicId}`), { headers, credentials: "include" }),
        fetch(buildApiUrl(`doctors/capacity/${clinicId}`), { headers, credentials: "include" }),
        fetch(buildApiUrl(`subscription/dashboard/${clinicId}`), { headers, credentials: "include" }),
      ]);
      if (!doctorResponse.ok) throw new Error("Unable to load doctors");
      const doctorData = await doctorResponse.json();
      const liveDoctors = Array.isArray(doctorData) ? doctorData.map(mapDoctor) : [];
      setDoctors(liveDoctors);
      setSelectedId((currentId) => currentId && liveDoctors.some((doctor) => doctor.id === currentId) ? currentId : null);
      const photoMap = {};
      await Promise.all(liveDoctors.map(async (doctor) => {
        if (!doctor.id) return;
        try {
          const response = await fetch(buildApiUrl(`doctors/${doctor.id}/photo/content`), { headers, credentials: "include" });
          if (response.ok) {
            const blob = await response.blob();
            photoMap[doctor.id] = URL.createObjectURL(blob);
          }
        } catch {
          photoMap[doctor.id] = "";
        }
      }));
      setDoctorPhotos(photoMap);
      if (capacityResponse.ok) setCapacity(await capacityResponse.json());
      if (subscriptionResponse.ok) {
        const subscription = await subscriptionResponse.json();
        setPatientReceiptsAvailable(subscription?.patientReceiptEnabled === true || String(subscription?.planName || "").toLowerCase() === "flex pro");
      }
    } catch {
      setNotice("Unable to load live doctor data");
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => { loadBackendDoctors(); }, [loadBackendDoctors]);

  const loadDoctorAssets = useCallback(async () => {
    if (!clinicId || !selectedId) {
      applyPhoto("");
      return;
    }
    try {
      const response = await fetch(buildApiUrl(`doctors/${selectedId}/photo/content`), { headers: assetHeaders, credentials: "include" });
      if (response.ok) applyPhoto(URL.createObjectURL(await response.blob()));
      else applyPhoto("");
    } catch {
      applyPhoto("");
    }
  }, [assetHeaders, clinicId, selectedId, applyPhoto]);

  useEffect(() => {
    loadDoctorAssets();
  }, [loadDoctorAssets]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(""), 2800);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onKey = (event) => { if (event.key === "Escape") setPanelOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen]);

  const filteredDoctors = useMemo(() => doctors.filter((doctor) => {
    const query = search.toLowerCase().trim();
    return (!query || doctor.name.toLowerCase().includes(query) || doctor.specialization.toLowerCase().includes(query))
      && (specialization === "All Specializations" || doctor.specialization === specialization)
      && (status === "All Status" || doctor.status === status);
  }), [doctors, search, specialization, status]);

  const specializationOptions = useMemo(() => [
    "All Specializations",
    ...Array.from(new Set(doctors.map((doctor) => doctor.specialization).filter(Boolean))).sort(),
  ], [doctors]);

  const formSpecializations = useMemo(
    () => (form.specialization && !SPECIALIZATIONS.includes(form.specialization) ? [form.specialization, ...SPECIALIZATIONS] : SPECIALIZATIONS),
    [form.specialization],
  );

  const avgFee = doctors.length
    ? Math.round(doctors.reduce((sum, doctor) => sum + (Number(doctor.fee) || 0), 0) / doctors.length)
    : 0;

  const stats = {
    total: doctors.length,
    active: doctors.filter((doctor) => doctor.status === "Active").length,
    inactive: doctors.filter((doctor) => doctor.status === "Inactive").length,
    avgFee,
  };

  const updateForm = (patch) => { setForm((current) => ({ ...current, ...patch })); setDirty(true); };
  const openDoctor = (id) => {
    const doctor = doctors.find((item) => item.id === id);
    if (!doctor) return;
    setCreating(false); setSelectedId(id); setForm({ ...doctor, schedule: { ...doctor.schedule }, prescriptionSchedule: normalizePrescriptionSchedule(doctor.prescriptionSchedule) }); setPanelOpen(true); setActiveTab("personal"); setDirty(false);
  };
  const openCreate = () => {
    if (capacity && !capacity.unlimited && !capacity.canAddDoctor) {
      setNotice("Your doctor limit has been reached. Add doctor slots from the subscription page first.");
      return;
    }
    setCreating(true); setSelectedId(null); setForm(emptyDoctor()); setPanelOpen(true); setActiveTab("personal"); setDirty(false);
  };
  const saveDoctor = async (event) => {
    event?.preventDefault();
    if (!form.name.trim()) { setNotice("Full name is required"); setActiveTab("personal"); return; }
    setLoading(true);
    const payload = {
      clinicId,
      doctorName: form.name,
      specialization: form.specialization,
      qualification: form.qualification,
      gender: form.gender,
      consultationFee: Number(form.fee || 0),
      registrationNumber: form.registrationNumber,
      experienceYears: form.experience,
      doctorPhone: form.mobile,
      doctorEmail: form.email,
      upiId: form.upiId,
      upiPayeeName: form.upiPayeeName,
      enableUpiPayments: form.enableUpiPayments,
      enableCashPayments: form.enableCashPayments,
      enablePatientReceipts: patientReceiptsAvailable && form.enablePatientReceipts,
      defaultPrescriptionTimings: "",
      defaultPrescriptionSchedule: JSON.stringify(form.prescriptionSchedule),
      enableEPrescription: form.enableEPrescription,
      enableWhatsAppNotifications: form.enableWhatsAppNotifications,
    };
    try {
      const response = await fetch(buildApiUrl(creating ? "doctors/add" : `doctors/update/${selectedId}`), {
        method: creating ? "POST" : "PUT",
        headers: { "Content-Type": "application/json", "X-Clinic-Id": String(clinicId) },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to save doctor");
      setNotice(creating ? "Doctor added successfully" : "Doctor details updated");
      setDirty(false);
      setCreating(false);
      await loadBackendDoctors();
    } catch (error) {
      setNotice(error.message || "Unable to save doctor");
    } finally {
      setLoading(false);
    }
  };
  const toggleStatus = async () => {
    if (creating || !selectedId) return;
    try {
      const response = await fetch(buildApiUrl(`doctors/toggle/${selectedId}`), { method: "PUT", headers: { "X-Clinic-Id": String(clinicId) }, credentials: "include" });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to update status");
      await loadBackendDoctors();
      setNotice("Doctor status updated");
    } catch (error) { setNotice(error.message || "Unable to update status"); }
  };
  const updatePrescriptionSession = (day, session, field, value) => updateForm({
    prescriptionSchedule: {
      ...form.prescriptionSchedule,
      [day]: { ...form.prescriptionSchedule[day], [session]: { ...form.prescriptionSchedule[day][session], [field]: value } },
    },
  });
  const togglePrescriptionDay = (day) => updateForm({
    prescriptionSchedule: { ...form.prescriptionSchedule, [day]: { ...form.prescriptionSchedule[day], holiday: !form.prescriptionSchedule[day].holiday } },
  });
  const uploadPhoto = async (file) => {
    if (!file || !selectedId) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const response = await fetch(buildApiUrl(`doctors/${selectedId}/photo`), {
        method: "POST", headers: assetHeaders, credentials: "include", body,
      });
      const result = await response.json();
      if (!response.ok || result.success === false) throw new Error(result.message || "Upload failed");
      setNotice("Doctor photo updated");
      await loadDoctorAssets();
    } catch (error) { setNotice(error.message || "Upload failed"); }
  };

  const noticeIsError = /unable|required|failed|limit/i.test(notice);
  const capacityLabel = capacity?.unlimited
    ? `${capacity.activeDoctorCount || doctors.length} / Unlimited`
    : `${capacity?.activeDoctorCount || doctors.length} / ${capacity?.allowedDoctors || "-"}`;

  /* --------------------------- render ----------------------------- */
  return (
    <div className="dm-root">
      <style>{STYLES}</style>
      <input ref={photoInputRef} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { uploadPhoto(event.target.files?.[0]); event.target.value = ""; }} />

      <main className="dm-list-pane">
        <header className="dm-header">
          <div className="dm-header-text">
            <div className="dm-eyebrow"><Stethoscope size={14} /> Clinic workspace</div>
            <h1>Doctors</h1>
            <p>Manage your clinic doctors, schedules and consultation settings.</p>
          </div>
          <div className="dm-header-actions">
            <span className="dm-capacity" title="Doctors in use out of your plan limit"><Users size={15} />{capacityLabel} doctors</span>
            <button className="dm-primary" onClick={openCreate}><Plus size={17} /> Add New Doctor</button>
          </div>
        </header>

        <section className="dm-stats">
          {[[Users, stats.total, "Total Doctors", "blue"], [UserCheck, stats.active, "Active", "green"], [UserX, stats.inactive, "Inactive", "red"], [Wallet, `₹${stats.avgFee}`, "Avg Fee", "violet"]].map(([Icon, value, label, tone]) => (
            <article className="dm-stat" key={label}>
              <span className={`dm-stat-icon ${tone}`}><Icon size={20} /></span>
              <div><strong>{value}</strong><small>{label}</small></div>
            </article>
          ))}
        </section>

        <div className="dm-filters">
          <label className="dm-search">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search doctors by name or specialization" />
          </label>
          <label className="dm-select">
            <select value={specialization} onChange={(event) => setSpecialization(event.target.value)}>
              {specializationOptions.map((item) => <option key={item}>{item}</option>)}
            </select>
            <ChevronDown size={15} />
          </label>
          <label className="dm-select">
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {getDoctorStatusOptions().map((option) => <option key={option}>{option}</option>)}
            </select>
            <ChevronDown size={15} />
          </label>
        </div>

        <section className="dm-doctor-list">
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div className="dm-card dm-skeleton-card" key={`doctor-skeleton-${index}`} aria-hidden="true">
                <span className="dm-skeleton dm-skeleton-avatar" />
                <div className="dm-skeleton-content">
                  <span className="dm-skeleton dm-skeleton-line long" />
                  <span className="dm-skeleton dm-skeleton-line medium" />
                  <span className="dm-skeleton dm-skeleton-line short" />
                </div>
                <div className="dm-skeleton-meta">
                  <span className="dm-skeleton dm-skeleton-box" />
                  <span className="dm-skeleton dm-skeleton-box" />
                  <span className="dm-skeleton dm-skeleton-box" />
                </div>
                <span className="dm-skeleton dm-skeleton-more" />
              </div>
            ))
          ) : filteredDoctors.map((doctor) => (
            <article className={`dm-card${selectedId === doctor.id && panelOpen ? " selected" : ""}`} key={doctor.id} onClick={() => openDoctor(doctor.id)}>
              <Avatar src={doctorPhotos[doctor.id] || ""} alt={doctor.name} initials={getInitials(doctor.name)} />
              <div className="dm-card-main">
                <div className="dm-card-name">
                  <strong>{doctor.name}</strong>
                  <StatusPill status={doctor.status} />
                </div>
                <b>{doctor.specialization}</b>
                <small>
                  {doctor.qualification}
                  {doctor.qualification && doctor.experience !== "" && <i>•</i>}
                  {doctor.experience !== "" && <>{doctor.experience} yrs exp</>}
                </small>
              </div>
              <div className="dm-meta">
                <div className="dm-meta-item"><span className="dm-meta-icon"><Users size={15} /></span><div><strong>{doctor.patientsToday}</strong><small>Patients today</small></div></div>
                <div className="dm-meta-item"><span className="dm-meta-icon"><Wallet size={15} /></span><div><strong>₹{doctor.fee}</strong><small>Consultation fee</small></div></div>
                <div className="dm-meta-item dm-hours"><span className="dm-meta-icon"><CalendarClock size={15} /></span><div><strong>{doctor.workingHours || "Daily consults"}</strong><small>Schedule</small></div></div>
              </div>
              <button className="dm-more" onClick={(event) => event.stopPropagation()} aria-label={`Actions for ${doctor.name}`}><MoreVertical size={17} /></button>
            </article>
          ))}
          {!loading && !filteredDoctors.length && (
            <div className="dm-empty"><Stethoscope size={26} /><strong>No doctors match your filters</strong><span>Try a different search term or clear the filters.</span></div>
          )}
        </section>
        <p className="dm-showing">Showing {filteredDoctors.length} {filteredDoctors.length === 1 ? "doctor" : "doctors"}</p>
      </main>

      {panelOpen && (
        <>
          <div className="dm-overlay" onClick={() => setPanelOpen(false)} />
          <aside className="dm-panel" role="dialog" aria-modal="true" aria-label={creating ? "Add new doctor" : "Edit doctor"}>
            <header className="dm-panel-header">
              <h2>{creating ? "Add New Doctor" : "Edit Doctor"}</h2>
              <button className="dm-close" onClick={() => setPanelOpen(false)} aria-label="Close panel"><X size={19} /></button>
            </header>

            <div className="dm-hero">
              <div className="dm-avatar-wrap">
                <Avatar size={68} src={photoUrl} alt={`${form.name || "Doctor"} profile`} initials={getInitials(form.name)} />
                <button type="button" className="dm-camera" disabled={!selectedId} onClick={() => photoInputRef.current?.click()} aria-label="Change photo"><Camera size={13} /></button>
              </div>
              <div className="dm-hero-info">
                <strong>{form.name || "New Doctor"}</strong>
                <span>{form.specialization}</span>
                <StatusPill status={form.status} />
              </div>
              <button type="button" className="dm-ghost" disabled={!selectedId} title={selectedId ? "" : "Save the doctor first to add a photo"} onClick={() => photoInputRef.current?.click()}>
                <Camera size={14} /> Change photo
              </button>
            </div>

            <nav className="dm-tabs" role="tablist">
              {TABS.map(([id, Icon, label]) => (
                <button type="button" role="tab" aria-selected={activeTab === id} className={activeTab === id ? "active" : ""} key={id} onClick={() => setActiveTab(id)}><Icon size={14} />{label}</button>
              ))}
            </nav>

            <form className="dm-form" onSubmit={saveDoctor}>
              {activeTab === "personal" && (
                <>
                  <SectionTitle icon={User} title="Basic Information" subtitle="Doctor's personal and contact details." />
                  <div className="dm-grid">
                    <Field label="Full Name" required><input value={form.name} onChange={(event) => updateForm({ name: event.target.value })} placeholder="Dr. Full Name" /></Field>
                    <Field label="Gender" chevron><select value={form.gender} onChange={(event) => updateForm({ gender: event.target.value })}><option>Male</option><option>Female</option><option>Other</option></select></Field>
                    <Field label="Mobile Number" icon={Phone}><input value={form.mobile} onChange={(event) => updateForm({ mobile: event.target.value })} placeholder="+91 90000 00000" /></Field>
                    <Field label="Email Address" icon={Mail}><input value={form.email} onChange={(event) => updateForm({ email: event.target.value })} type="email" placeholder="doctor@clinic.com" /></Field>
                  </div>
                  <div className="dm-divider" />
                  <SectionTitle icon={Stethoscope} title="Professional Details" subtitle="Specialization, qualification and experience." />
                  <div className="dm-grid">
                    <Field label="Specialization" chevron><select value={form.specialization} onChange={(event) => updateForm({ specialization: event.target.value })}>{formSpecializations.map((item) => <option key={item}>{item}</option>)}</select></Field>
                    <Field label="Qualification"><input value={form.qualification} onChange={(event) => updateForm({ qualification: event.target.value })} placeholder="MBBS, MD" /></Field>
                    <Field label="Years of Experience"><input type="number" min="0" value={form.experience} onChange={(event) => updateForm({ experience: event.target.value })} placeholder="0" /></Field>
                    <Field label="Registration Number"><input value={form.registrationNumber} onChange={(event) => updateForm({ registrationNumber: event.target.value })} placeholder="Medical Reg. No." /></Field>
                  </div>
                </>
              )}

              {activeTab === "schedule" && (
                <>
                  <SectionTitle icon={CalendarClock} title="Prescription Shift" subtitle="These timings appear on prescriptions, not OPD appointment slots." />
                  <div className="dm-schedule">
                    {WEEKDAYS.map((day) => {
                      const config = form.prescriptionSchedule[day] || { ...createEmptyPrescriptionSchedule()[day] };
                      return (
                        <div className={`dm-day${config.holiday ? " off" : ""}`} key={day}>
                          <label className="dm-day-head">
                            <Switch checked={!config.holiday} onChange={() => togglePrescriptionDay(day)} />
                            <strong>{day}</strong>
                          </label>
                          <div className="dm-day-body">
                            {config.holiday ? <span className="dm-closed">Closed</span> : ["session1", "session2"].map((session, index) => (
                              <div className="dm-session" key={session}>
                                <span className="dm-session-tag">Shift {index + 1}</span>
                                <input inputMode="numeric" maxLength={2} value={config[session].from} onChange={(event) => updatePrescriptionSession(day, session, "from", event.target.value)} placeholder="09" aria-label={`${day} shift ${index + 1} start hour`} />
                                <b>:</b>
                                <input inputMode="numeric" maxLength={2} value={config[session].fromMinute} onChange={(event) => updatePrescriptionSession(day, session, "fromMinute", event.target.value)} placeholder="00" aria-label={`${day} shift ${index + 1} start minute`} />
                                <select value={config[session].fromPeriod} onChange={(event) => updatePrescriptionSession(day, session, "fromPeriod", event.target.value)}><option>AM</option><option>PM</option></select>
                                <em>to</em>
                                <input inputMode="numeric" maxLength={2} value={config[session].to} onChange={(event) => updatePrescriptionSession(day, session, "to", event.target.value)} placeholder="01" aria-label={`${day} shift ${index + 1} end hour`} />
                                <b>:</b>
                                <input inputMode="numeric" maxLength={2} value={config[session].toMinute} onChange={(event) => updatePrescriptionSession(day, session, "toMinute", event.target.value)} placeholder="00" aria-label={`${day} shift ${index + 1} end minute`} />
                                <select value={config[session].toPeriod} onChange={(event) => updatePrescriptionSession(day, session, "toPeriod", event.target.value)}><option>AM</option><option>PM</option></select>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {activeTab === "fees" && (
                <>
                  <SectionTitle icon={Wallet} title="Consultation Fees" subtitle="Set the consultation fee and UPI payment details." />
                  <div className="dm-grid">
                    <Field label="Consultation Fee" prefix="₹"><input type="number" min="0" value={form.fee} onChange={(event) => updateForm({ fee: event.target.value })} placeholder="0" /></Field>
                    <Field label="UPI ID"><input value={form.upiId} onChange={(event) => updateForm({ upiId: event.target.value })} placeholder="doctor@upi" /></Field>
                    <Field label="UPI Payee Name"><input value={form.upiPayeeName} onChange={(event) => updateForm({ upiPayeeName: event.target.value })} placeholder="Dr. Full Name" /></Field>
                  </div>
                </>
              )}

              {activeTab === "features" && (
                <>
                  <SectionTitle icon={CheckCircle2} title="Payment & Features" subtitle="Control payment methods and doctor-specific clinic features." />
                  <div className="dm-settings">
                    {FEATURES.map(([label, key, Icon, hint]) => {
                      const locked = key === "enablePatientReceipts" && !patientReceiptsAvailable;
                      return (
                        <label className={`dm-setting${locked ? " locked" : ""}`} key={key}>
                          <span className="dm-setting-icon"><Icon size={17} /></span>
                          <span className="dm-setting-text">
                            <strong>{label}{locked && <em className="dm-flex-pro"><Crown size={11} /> Flex Pro</em>}</strong>
                            <small>{locked ? "Available only on Flex Pro" : hint}</small>
                          </span>
                          <Switch disabled={locked} checked={Boolean(form[key]) && !locked} onChange={() => updateForm({ [key]: !form[key] })} />
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              {activeTab === "vitals" && (
                <DoctorVitalsSettings doctorId={selectedId} clinicId={clinicId} />
              )}
            </form>

            <footer className="dm-panel-footer">
              {creating ? <span /> : <button className="dm-danger-button" type="button" onClick={toggleStatus}><Ban size={15} /> {form.status === "Inactive" ? "Reactivate" : "Deactivate"}</button>}
              <div className="dm-footer-actions">
                <button className="dm-secondary" type="button" onClick={() => setPanelOpen(false)}>Cancel</button>
                <button className="dm-primary" type="button" disabled={(!dirty && !creating) || loading} onClick={saveDoctor}><Check size={15} /> {creating ? "Add Doctor" : "Update Doctor"}</button>
              </div>
            </footer>
          </aside>
        </>
      )}

      {notice && <div className={`dm-toast${noticeIsError ? " error" : ""}`} role="status">{noticeIsError ? <AlertCircle size={17} /> : <CheckCircle2 size={17} />}{notice}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.dm-root{
  --ink:#0f172a; --soft:#475569; --muted:#8492a6; --line:#e6ebf3; --line-strong:#d5dce9;
  --blue:#2563eb; --indigo:#4f46e5; --blue-soft:#eef3ff; --surface:#ffffff; --field:#f8fafd;
  --shadow-sm:0 1px 2px rgba(15,23,42,.04),0 6px 18px -8px rgba(15,23,42,.10);
  --shadow-md:0 2px 4px rgba(15,23,42,.04),0 16px 34px -14px rgba(37,99,235,.28);
  min-height:100%; color:var(--ink);
  font-family:'Plus Jakarta Sans',system-ui,-apple-system,'Segoe UI',sans-serif;
  background:linear-gradient(150deg,#f3f7ff 0%,#f8fafc 52%,#eef9f7 100%);
  -webkit-font-smoothing:antialiased;
}
.dm-root *,.dm-root *::before,.dm-root *::after{box-sizing:border-box}
.dm-root button,.dm-root input,.dm-root select{font-family:inherit;letter-spacing:normal}
.dm-root button{cursor:pointer}
.dm-root :focus-visible{outline:2px solid var(--blue);outline-offset:2px}

/* neutral, thin scrollbars (replaces the heavy teal ones) */
.dm-root *{scrollbar-width:thin;scrollbar-color:#cbd5e1 transparent}
.dm-root ::-webkit-scrollbar{width:8px;height:8px;background:transparent}
.dm-root ::-webkit-scrollbar-track{background:transparent}
.dm-root ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:8px;border:2px solid transparent;background-clip:content-box}
.dm-root .dm-tabs{scrollbar-width:none}
.dm-root .dm-tabs::-webkit-scrollbar{display:none}

/* ---------------- list pane ---------------- */
.dm-list-pane{container:dm-list / inline-size;padding:34px clamp(20px,3.2vw,44px) 48px;max-width:1280px;margin:0 auto;width:100%}

.dm-header{display:flex;justify-content:space-between;align-items:flex-end;gap:20px;margin-bottom:26px}
.dm-header-text{min-width:0}
.dm-eyebrow{display:inline-flex;align-items:center;gap:7px;color:var(--blue);font-size:12px;font-weight:700}
.dm-root .dm-header h1{margin:8px 0 6px;font-size:32px;line-height:1.1;letter-spacing:-.035em;font-weight:800;color:var(--ink)}
.dm-root .dm-header p{margin:0;color:var(--soft);font-size:14px;line-height:1.5}
.dm-header-actions{display:flex;align-items:center;gap:12px;flex:none}
.dm-capacity{display:inline-flex;align-items:center;gap:7px;height:42px;padding:0 14px;border:1px solid var(--line);border-radius:11px;background:#fff;color:var(--soft);font-size:12px;font-weight:700;white-space:nowrap;box-shadow:var(--shadow-sm)}
.dm-capacity svg{color:var(--blue)}

.dm-root .dm-primary{display:inline-flex;align-items:center;justify-content:center;gap:8px;height:42px;padding:0 18px;border:0;border-radius:11px;color:#fff;font-size:13px;font-weight:700;white-space:nowrap;background:linear-gradient(135deg,#3b82f6 0%,#2563eb 55%,#4f46e5 100%);box-shadow:0 10px 22px -10px rgba(37,99,235,.75),inset 0 1px 0 rgba(255,255,255,.25);transition:transform .15s ease,box-shadow .15s ease,opacity .15s ease}
.dm-root .dm-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 14px 26px -10px rgba(37,99,235,.85),inset 0 1px 0 rgba(255,255,255,.25)}
.dm-root .dm-primary:disabled{opacity:.5;cursor:not-allowed;box-shadow:none}

.dm-skeleton-card{display:grid;grid-template-columns:52px minmax(180px,1.2fr) minmax(0,2fr) 34px;align-items:center;column-gap:18px;padding:16px 18px;border-radius:16px;overflow:hidden}
.dm-skeleton{display:block;position:relative;overflow:hidden;border-radius:12px;background:linear-gradient(90deg,#edf2f7 25%,#e3ebf5 50%,#edf2f7 75%);background-size:200% 100%;animation:dm-skeleton-shimmer 1.35s ease infinite}
@keyframes dm-skeleton-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
.dm-skeleton-avatar{width:52px;height:52px;border-radius:50%}
.dm-skeleton-content{display:flex;flex-direction:column;gap:9px}
.dm-skeleton-line{height:12px}
.dm-skeleton-line.long{width:72%}
.dm-skeleton-line.medium{width:58%}
.dm-skeleton-line.short{width:40%}
.dm-skeleton-meta{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
.dm-skeleton-box{height:46px;border-radius:12px}
.dm-skeleton-more{width:34px;height:34px;border-radius:10px}

/* stats */
.dm-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;margin-bottom:22px}
.dm-stat{display:flex;align-items:center;gap:14px;padding:18px 20px;background:var(--surface);border:1px solid var(--line);border-radius:16px;box-shadow:var(--shadow-sm);min-width:0}
.dm-stat-icon{display:grid;place-items:center;flex:none;width:44px;height:44px;border-radius:13px}
.dm-stat-icon.blue{background:#e8f0ff;color:#2563eb}
.dm-stat-icon.green{background:#e6f7ee;color:#16a34a}
.dm-stat-icon.amber{background:#fff3df;color:#d97706}
.dm-stat-icon.red{background:#feeaea;color:#dc2626}
.dm-stat strong{display:block;font-size:26px;line-height:1;font-weight:800;letter-spacing:-.02em}
.dm-stat small{display:block;margin-top:6px;color:var(--muted);font-size:12px;font-weight:600;white-space:nowrap}

/* filters */
.dm-filters{display:grid;grid-template-columns:minmax(0,1fr) 230px 190px;gap:12px;margin-bottom:20px}
.dm-search,.dm-select{position:relative;display:flex;align-items:center;gap:10px;height:46px;padding:0 14px;background:#fff;border:1px solid var(--line);border-radius:12px;color:var(--muted);box-shadow:var(--shadow-sm);transition:border-color .15s,box-shadow .15s}
.dm-search:focus-within,.dm-select:focus-within{border-color:#93b4ff;box-shadow:0 0 0 3px var(--blue-soft)}
.dm-root .dm-search input{flex:1;min-width:0;height:100%;border:0;outline:0;background:transparent;font-size:13px;font-weight:500;color:var(--ink)}
.dm-root .dm-search input::placeholder{color:var(--muted)}
.dm-root .dm-search{background:linear-gradient(180deg,#fff,#f9fbff)}
.dm-root .dm-select select{flex:1;min-width:0;height:100%;padding:0 22px 0 0;border:0;outline:0;background:transparent;appearance:none;-webkit-appearance:none;font-size:13px;font-weight:700;color:var(--ink);cursor:pointer}
.dm-select svg{position:absolute;right:14px;pointer-events:none}

/* doctor cards */
.dm-doctor-list{display:flex;flex-direction:column;gap:12px}
.dm-card{display:grid;grid-template-columns:52px minmax(190px,1.15fr) minmax(0,2fr) 34px;align-items:center;column-gap:18px;row-gap:14px;padding:18px 20px;background:linear-gradient(180deg,#ffffff 0%,#f9fbff 100%);border:1px solid var(--line);border-radius:18px;box-shadow:0 10px 24px -18px rgba(30,41,59,.26);cursor:pointer;transition:border-color .16s,box-shadow .16s,transform .16s}
.dm-card:hover{border-color:#b8ccff;box-shadow:0 16px 30px -20px rgba(37,99,235,.32);transform:translateY(-1px)}
.dm-card.selected{border-color:#7ea1ff;box-shadow:0 0 0 3px var(--blue-soft),0 16px 34px -22px rgba(37,99,235,.42)}
.dm-avatar{display:grid;place-items:center;flex:none;overflow:hidden;border-radius:50%;background:linear-gradient(135deg,#e6eeff,#dfe6f7);color:#4f6bb5;font-weight:800;border:2px solid #fff;box-shadow:0 0 0 1px var(--line-strong),0 8px 20px -12px rgba(37,99,235,.45)}
.dm-avatar img{display:block;width:100%;height:100%;object-fit:cover}
.dm-card-main{min-width:0}
.dm-card-name{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.dm-card-name strong{font-size:15px;font-weight:700;letter-spacing:-.01em;overflow-wrap:anywhere}
.dm-card-main>b{display:block;margin-top:4px;color:var(--soft);font-size:12.5px;font-weight:600}
.dm-card-main>small{display:block;margin-top:3px;color:var(--muted);font-size:12px;font-weight:500}
.dm-card-main>small i{font-style:normal;margin:0 6px;opacity:.7}
.dm-status{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;white-space:nowrap;line-height:1.3}
.dm-status i{width:6px;height:6px;border-radius:50%;flex:none}

.dm-meta{display:grid;grid-template-columns:minmax(100px,.8fr) minmax(120px,.9fr) minmax(140px,1.3fr);gap:10px 18px;align-items:center;min-width:0}
.dm-meta-item{display:flex;align-items:center;gap:11px;min-width:0}
.dm-meta-icon{display:grid;place-items:center;flex:none;width:34px;height:34px;border-radius:10px;background:#f1f5fb;color:#64748b}
.dm-meta-item>div{min-width:0}
.dm-meta-item strong{display:block;font-size:13.5px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.dm-meta-item small{display:block;margin-top:2px;color:var(--muted);font-size:11.5px;font-weight:500;white-space:nowrap}
.dm-more{display:grid;place-items:center;width:34px;height:34px;border:1px solid transparent;border-radius:10px;background:transparent;color:var(--soft);transition:background .15s,border-color .15s}
.dm-more:hover{background:#f1f4f9;border-color:var(--line)}
.dm-showing{margin:16px 2px 0;color:var(--muted);font-size:12px;font-weight:500}

.dm-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;min-height:190px;padding:40px 20px;text-align:center;background:var(--surface);border:1px dashed var(--line-strong);border-radius:16px;color:var(--blue)}
.dm-empty strong{margin-top:8px;color:var(--ink);font-size:14px}
.dm-empty span{color:var(--muted);font-size:12.5px}

/* container-driven responsiveness for the list */
@container dm-list (max-width:900px){
  .dm-card{grid-template-columns:52px minmax(0,1fr) 34px;grid-template-areas:"avatar main more" "meta meta meta"}
  .dm-card>.dm-avatar{grid-area:avatar}
  .dm-card>.dm-card-main{grid-area:main}
  .dm-card>.dm-more{grid-area:more}
  .dm-card>.dm-meta{grid-area:meta;padding-top:14px;border-top:1px solid var(--line)}
}
@container dm-list (max-width:720px){
  .dm-header{flex-direction:column;align-items:stretch}
  .dm-header-actions{justify-content:space-between}
  .dm-stats{grid-template-columns:repeat(2,minmax(0,1fr))}
  .dm-filters{grid-template-columns:1fr 1fr}
  .dm-filters .dm-search{grid-column:1/-1}
}
@container dm-list (max-width:520px){
  .dm-list-pane{padding:18px 14px 28px}
  .dm-header{margin-bottom:18px}
  .dm-header-actions{flex-direction:column;align-items:stretch;gap:10px}
  .dm-capacity{justify-content:center}
  .dm-stats{grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
  .dm-stat{padding:14px 12px}
  .dm-stat strong{font-size:22px}
  .dm-filters{grid-template-columns:1fr}
  .dm-meta{grid-template-columns:1fr 1fr;gap:10px}
  .dm-hours{grid-column:1/-1}
  .dm-card{grid-template-columns:46px minmax(0,1fr);column-gap:12px;padding:14px 12px}
  .dm-card>.dm-more{grid-column:2;justify-self:end;margin-top:-2px}
  .dm-card>.dm-meta{grid-column:1/-1;grid-template-columns:1fr 1fr;gap:10px;padding-top:10px;border-top:1px solid var(--line)}
  .dm-card-main{padding-right:8px}
}
@media (max-width:420px){
  .dm-list-pane{padding:14px 12px 22px}
  .dm-header-text h1{font-size:26px}
  .dm-header-text p{font-size:12.5px}
  .dm-stats{grid-template-columns:1fr 1fr}
  .dm-stat{min-height:88px}
  .dm-card{grid-template-columns:42px minmax(0,1fr)}
  .dm-card-name{flex-wrap:wrap}
  .dm-card-main>b,.dm-card-main>small{font-size:11.5px}
  .dm-meta{grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px 8px}
  .dm-hours{grid-column:1/-1}
  .dm-meta-item{gap:8px}
  .dm-meta-icon{width:32px;height:32px}
  .dm-meta-item strong{font-size:12.5px}
  .dm-meta-item small{font-size:10.5px}
  .dm-more{width:30px;height:30px}
}

/* ---------------- drawer ---------------- */
.dm-overlay{position:fixed;inset:0;z-index:1000;background:rgba(15,23,42,.38);backdrop-filter:blur(3px);animation:dm-fade .2s ease both}
.dm-panel{container:dm-panel / inline-size;position:fixed;top:0;right:0;bottom:0;z-index:1001;width:min(580px,100vw);height:100vh;height:100dvh;display:flex;flex-direction:column;background:#fff;box-shadow:-24px 0 60px -20px rgba(15,23,42,.35);animation:dm-slide .26s cubic-bezier(.2,.8,.2,1) both}
@keyframes dm-slide{from{transform:translateX(32px);opacity:0}to{transform:none;opacity:1}}
@keyframes dm-fade{from{opacity:0}to{opacity:1}}

.dm-panel-header{display:flex;align-items:center;justify-content:space-between;flex:none;padding:22px 26px 6px}
.dm-root .dm-panel-header h2{margin:0;font-size:21px;font-weight:800;letter-spacing:-.025em}
.dm-close{display:grid;place-items:center;width:36px;height:36px;border:1px solid var(--line);border-radius:10px;background:#fff;color:var(--soft);transition:background .15s}
.dm-close:hover{background:#f1f4f9}

.dm-hero{display:flex;align-items:center;gap:16px;flex:none;margin:14px 26px 0;padding:16px 18px;border:1px solid var(--line);border-radius:16px;background:linear-gradient(135deg,#f3f7ff 0%,#f9fbff 60%,#effaf8 100%)}
.dm-avatar-wrap{position:relative;flex:none}
.dm-camera{position:absolute;right:-4px;bottom:-2px;display:grid;place-items:center;width:26px;height:26px;border:2px solid #fff;border-radius:50%;background:var(--blue);color:#fff;box-shadow:0 4px 10px -2px rgba(37,99,235,.6)}
.dm-camera:disabled{background:#94a3b8;cursor:not-allowed;box-shadow:none}
.dm-hero-info{flex:1;min-width:0;display:flex;flex-direction:column;align-items:flex-start;gap:4px}
.dm-hero-info strong{max-width:100%;font-size:17px;font-weight:800;letter-spacing:-.02em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.dm-hero-info>span{color:var(--muted);font-size:12.5px;font-weight:600}
.dm-hero-info .dm-status{margin-top:4px}
.dm-ghost{display:inline-flex;align-items:center;gap:7px;flex:none;height:36px;padding:0 13px;border:1px solid var(--line-strong);border-radius:10px;background:#fff;color:var(--ink);font-size:12px;font-weight:700;white-space:nowrap;transition:border-color .15s,color .15s}
.dm-ghost:hover:not(:disabled){border-color:var(--blue);color:var(--blue)}
.dm-ghost:disabled{opacity:.5;cursor:not-allowed}

.dm-tabs{display:flex;flex:none;margin:18px 26px 0;border-bottom:1px solid var(--line);overflow-x:auto}
.dm-tabs button{position:relative;display:inline-flex;align-items:center;justify-content:center;gap:7px;flex:1 1 auto;padding:12px 10px;border:0;background:transparent;color:var(--muted);font-size:12.5px;font-weight:700;white-space:nowrap;transition:color .15s}
.dm-tabs button:hover{color:var(--ink)}
.dm-tabs button.active{color:var(--blue)}
.dm-tabs button.active::after{content:"";position:absolute;left:8px;right:8px;bottom:-1px;height:2.5px;border-radius:3px 3px 0 0;background:linear-gradient(90deg,#3b82f6,#4f46e5)}

.dm-form{flex:1 1 auto;min-height:0;overflow-y:auto;padding:22px 26px 28px}
.dm-section-title{display:flex;align-items:flex-start;gap:12px;margin:0 0 16px}
.dm-section-title>span{display:grid;place-items:center;flex:none;width:34px;height:34px;border-radius:10px;background:var(--blue-soft);color:var(--blue)}
.dm-section-title strong{display:block;font-size:14px;font-weight:700}
.dm-section-title small{display:block;margin-top:3px;color:var(--muted);font-size:12px;font-weight:500;line-height:1.4}
.dm-divider{height:1px;margin:26px 0 22px;background:var(--line)}

.dm-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px 14px}
.dm-field{display:flex;flex-direction:column;gap:7px;min-width:0;margin:0}
.dm-field.full{grid-column:1/-1}
.dm-label{color:var(--soft);font-size:12px;font-weight:700}
.dm-label b{margin-left:3px;color:#dc2626}
.dm-control{position:relative;display:flex;align-items:center;gap:8px;height:44px;padding:0 12px;border:1px solid var(--line-strong);border-radius:11px;background:var(--field);transition:border-color .15s,box-shadow .15s,background .15s}
.dm-control:hover{border-color:#b9c4d8}
.dm-control:focus-within{border-color:var(--blue);background:#fff;box-shadow:0 0 0 3px var(--blue-soft)}
.dm-root .dm-control input,.dm-root .dm-control select{flex:1;min-width:0;width:100%;height:100%;padding:0;border:0;outline:0;background:transparent;box-shadow:none;color:var(--ink);font-size:13px;font-weight:600}
.dm-root .dm-control input::placeholder{color:#a3aec0;font-weight:500}
.dm-root .dm-control select{appearance:none;-webkit-appearance:none;padding-right:22px;cursor:pointer}
.dm-control-icon{flex:none;color:var(--muted)}
.dm-chevron{position:absolute;right:12px;flex:none;color:var(--muted);pointer-events:none}
.dm-prefix{flex:none;font-style:normal;font-size:13px;font-weight:700;color:var(--soft);padding-right:8px;border-right:1px solid var(--line-strong);line-height:22px}

/* schedule */
.dm-schedule{display:flex;flex-direction:column;gap:10px}
.dm-day{display:grid;grid-template-columns:92px minmax(0,1fr);align-items:center;gap:12px;padding:12px 14px;border:1px solid var(--line);border-radius:13px;background:var(--field);transition:background .15s,border-color .15s}
.dm-day:not(.off){background:#fff;border-color:var(--line-strong)}
.dm-day-head{display:flex;align-items:center;gap:10px;margin:0;cursor:pointer}
.dm-day-head strong{font-size:13px;font-weight:700}
.dm-day.off .dm-day-head strong{color:var(--muted)}
.dm-day-body{display:flex;flex-direction:column;gap:8px;min-width:0}
.dm-closed{display:inline-flex;align-items:center;align-self:flex-start;padding:4px 10px;border-radius:999px;background:#eef1f6;color:var(--muted);font-size:11.5px;font-weight:700}
.dm-session{display:flex;align-items:center;gap:5px;flex-wrap:nowrap}
.dm-session-tag{flex:none;width:48px;color:var(--muted);font-size:11px;font-weight:600}
.dm-session b{color:var(--muted);font-size:12px}
.dm-session em{flex:none;padding:0 3px;color:var(--muted);font-size:11px;font-style:normal;font-weight:600}
.dm-root .dm-session input,.dm-root .dm-session select{height:32px;border:1px solid var(--line-strong);border-radius:8px;background-color:var(--field);color:var(--ink);font-size:12px;font-weight:600;text-align:center;outline:0;transition:border-color .15s,box-shadow .15s}
.dm-root .dm-session input{width:36px;padding:0}
.dm-root .dm-session select{width:54px;padding:0 16px 0 8px;text-align:left;appearance:none;-webkit-appearance:none;cursor:pointer;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 6px center}
.dm-root .dm-session input:focus,.dm-root .dm-session select:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-soft);background-color:#fff}

/* switch */
.dm-switch{position:relative;display:inline-flex;flex:none;width:40px;height:23px}
.dm-switch input{position:absolute;inset:0;z-index:1;width:100%;height:100%;margin:0;opacity:0;cursor:pointer}
.dm-switch i{position:absolute;inset:0;border-radius:999px;background:#cbd5e1;transition:background .2s}
.dm-switch i::after{content:"";position:absolute;top:3px;left:3px;width:17px;height:17px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(15,23,42,.3);transition:transform .2s cubic-bezier(.3,.7,.4,1)}
.dm-switch input:checked+i{background:linear-gradient(135deg,#3b82f6,#4f46e5)}
.dm-switch input:checked+i::after{transform:translateX(17px)}
.dm-switch input:disabled{cursor:not-allowed}
.dm-switch input:disabled+i{opacity:.55}
.dm-switch input:focus-visible+i{outline:2px solid var(--blue);outline-offset:2px}

/* settings */
.dm-settings{display:flex;flex-direction:column;gap:10px}
.dm-setting{display:flex;align-items:center;gap:14px;margin:0;padding:14px 16px;border:1px solid var(--line);border-radius:14px;background:#fff;cursor:pointer;transition:border-color .15s,box-shadow .15s}
.dm-setting:hover{border-color:#b8ccff;box-shadow:var(--shadow-sm)}
.dm-setting.locked{background:var(--field);cursor:not-allowed}
.dm-setting.locked:hover{border-color:var(--line);box-shadow:none}
.dm-setting-icon{display:grid;place-items:center;flex:none;width:38px;height:38px;border-radius:11px;background:var(--blue-soft);color:var(--blue)}
.dm-setting.locked .dm-setting-icon{background:#eef1f6;color:#94a3b8}
.dm-setting-text{flex:1;min-width:0}
.dm-setting-text strong{display:flex;align-items:center;gap:8px;font-size:13.5px;font-weight:700}
.dm-setting-text small{display:block;margin-top:3px;color:var(--muted);font-size:12px;font-weight:500}
.dm-flex-pro{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#fff4dc;color:#b45309;font-size:10.5px;font-style:normal;font-weight:700}

/* footer */
.dm-panel-footer{display:flex;align-items:center;justify-content:space-between;gap:12px;flex:none;padding:16px 26px calc(16px + env(safe-area-inset-bottom,0px));border-top:1px solid var(--line);background:rgba(255,255,255,.96)}
.dm-footer-actions{display:flex;gap:10px}
.dm-root .dm-secondary,.dm-root .dm-danger-button{display:inline-flex;align-items:center;justify-content:center;gap:7px;height:42px;padding:0 16px;border:1px solid var(--line-strong);border-radius:11px;background:#fff;color:var(--soft);font-size:13px;font-weight:700;white-space:nowrap;transition:background .15s,border-color .15s}
.dm-root .dm-secondary:hover{background:#f4f6fa}
.dm-root .dm-danger-button{background:#fff5f5;color:#dc2626;border-color:#f7d0d3}
.dm-root .dm-danger-button:hover{background:#feeaea;border-color:#f1b3b8}

/* toast */
.dm-toast{position:fixed;right:24px;bottom:24px;z-index:1100;display:flex;align-items:center;gap:10px;max-width:min(420px,calc(100vw - 32px));padding:13px 18px;border-radius:12px;background:#0f172a;color:#fff;font-size:13px;font-weight:600;line-height:1.4;box-shadow:0 20px 44px -14px rgba(15,23,42,.6);animation:dm-slide .25s ease both}
.dm-toast svg{flex:none;color:#4ade80}
.dm-toast.error svg{color:#fca5a5}

/* drawer responsiveness */
@container dm-panel (max-width:520px){
  .dm-grid{grid-template-columns:1fr}
  .dm-day{grid-template-columns:1fr;gap:10px}
  .dm-session{flex-wrap:wrap;row-gap:6px}
  .dm-hero{flex-wrap:wrap}
  .dm-ghost{display:none}
  .dm-tabs button{flex:none}
  .dm-panel-footer{flex-direction:column-reverse;align-items:stretch}
  .dm-footer-actions>button,.dm-panel-footer>.dm-danger-button{flex:1}
}
@media (max-width:640px){
  .dm-panel-header,.dm-form,.dm-panel-footer{padding-left:18px;padding-right:18px}
  .dm-hero,.dm-tabs{margin-left:18px;margin-right:18px}
}
@media (prefers-reduced-motion:reduce){
  .dm-root *{animation:none!important;transition:none!important}
}
`;